import { Navigate, Outlet } from "react-router";
import { useSession, type AppSession } from "@/app/session";
import { FullScreenLoadingState } from "@/components/data/loading-state";

/** Any signed-in session. */
type AuthedSession = Extract<
  AppSession,
  { status: "admin" | "company" | "driver" | "super_admin" }
>;

/**
 * Reproduces the Flutter app's go_router `redirect` table
 * (`app_router.dart`) as nested layout routes instead of one big callback.
 * See src/app/router.tsx for how these compose.
 */

/** No session → /sign-in. Renders the guarded subtree otherwise. */
export function RequireAuth() {
  const session = useSession();

  if (session.status === "loading") {
    return <FullScreenLoadingState />;
  }
  if (session.status === "unauthenticated") {
    return <Navigate to="/sign-in" replace />;
  }
  return <Outlet />;
}

/**
 * Session already present → /home. Used to wrap /sign-in and
 * /password-reset, which the Flutter app also redirects away from once
 * authenticated. Redirect-after-login always lands on /home (the Flutter
 * app has no "return to originally requested page" behavior — preserved
 * here deliberately, see MIGRATION.md).
 */
export function RedirectIfAuthenticated() {
  const session = useSession();

  if (session.status === "loading") {
    return <FullScreenLoadingState />;
  }
  if (session.status !== "unauthenticated") {
    return <Navigate to="/home" replace />;
  }
  return <Outlet />;
}

/**
 * Generic role/feature-flag gate. Renders the guarded subtree only when
 * `allow(session)` is true for an authenticated session; otherwise redirects
 * to /home. Assumes it's nested under `<RequireAuth />`, so `session` is
 * never `unauthenticated` here in practice — the check is kept anyway for
 * safety if this guard is ever used standalone.
 */
export function RequireSession({
  allow,
}: {
  allow: (session: AuthedSession) => boolean;
}) {
  const session = useSession();

  if (session.status === "loading") {
    return <FullScreenLoadingState />;
  }
  if (session.status === "unauthenticated") {
    return <Navigate to="/sign-in" replace />;
  }
  if (!allow(session)) {
    return <Navigate to="/home" replace />;
  }
  return <Outlet />;
}

// Role predicates shared by the router — kept here alongside the guards
// that consume them so the redirect table in router.tsx stays declarative.
export const isAdmin = (session: AuthedSession) =>
  session.status === "admin";

export const isSuperAdmin = (session: AuthedSession) =>
  session.status === "super_admin";

export const isCompany = (session: AuthedSession) =>
  session.status === "company";

export const isNotDriver = (session: AuthedSession) =>
  session.status !== "driver";

export const canUseMoneyCodes = (session: AuthedSession) => session.status === "admin" || (session.status === "company" && session.company.isMoneyCodeEnabled);
